/*
 * Apex price overrides.
 *
 * The apex calendar loads first with package prices per day. Later, the rooms endpoint returns the
 * real cheapest bookable price for the currently selected day. When that price differs from the apex
 * price for the same day, we persist an override so the calendar reflects the accurate price.
 *
 * An override can also record that a day turned out to have NO availability at all: the rooms
 * endpoint answering sold-out means the apex price for that day is not bookable, so the calendar must
 * drop it rather than keep advertising it (MPH-887). Those entries carry `unavailable` instead of a
 * price, which is why ApexPriceOverride is a union — an unavailable day has no price to store.
 *
 * Overrides are kept in localStorage with a 20-minute TTL and are scoped to the exact search the apex
 * call was made for (hotel, origin, travellers, nights, currency, business profile and the meal/flight
 * filters + flags). The specific day is the inner key, so a single search can hold one override per day.
 */

const STORAGE_KEY = 'apex_price_overrides';
const TTL_MS = 20 * 60 * 1000; // 20 minutes

export type ApexPriceOverride =
  // `unavailable?: false` (rather than omitted) keeps `if (override.unavailable)` narrowing to the
  // sold-out arm on both members.
  | { unavailable?: false; pricePerPerson: number; price: number; expiresAt: number }
  | { unavailable: true; expiresAt: number };

/** What a caller records for a day: the corrected price, or that the day is sold out. */
export type ApexPriceOverrideValue = { pricePerPerson: number; price: number } | { unavailable: true };

// outer key: scopeKey (the search identity) → inner key: dateKey (yyyy-MM-dd) → override
type ApexPriceOverrideStore = Record<string, Record<string, ApexPriceOverride>>;

type BuildApexScopeKeyParams = {
  internalHotelId: number;
  origin: string;
  adults: number;
  childrenAges: string;
  nights: number;
  currency: string;
  businessProfile: string;
  mealPlans: number[];
  flightTimesOutbound: number[];
  flightTimesInbound: number[];
  apexFlightsCheapestEnabled: boolean;
};

/**
 * Builds the identity of the apex search. Kept in sync with the apex calendar SWR key so the override
 * scope matches exactly the search the prices belong to. Only fields that parameterize the apex call
 * are included (the hotel id already determines the destination).
 */
export function buildApexScopeKey({
  internalHotelId,
  origin,
  adults,
  childrenAges,
  nights,
  currency,
  businessProfile,
  mealPlans,
  flightTimesOutbound,
  flightTimesInbound,
  apexFlightsCheapestEnabled,
}: BuildApexScopeKeyParams): string {
  return `${internalHotelId}-${origin}-${adults}-${childrenAges}-${nights}-${currency}-${businessProfile}-${mealPlans.join(',') || 'null'}-${flightTimesOutbound.join(',')}-${flightTimesInbound.join(',')}-${apexFlightsCheapestEnabled}`;
}

const isBrowser = () => typeof window !== 'undefined';

function readStore(): ApexPriceOverrideStore {
  if (!isBrowser()) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ApexPriceOverrideStore) : {};
  } catch {
    return {};
  }
}

function writeStore(store: ApexPriceOverrideStore): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore quota / serialization errors
  }
}

/** Drops expired entries. Returns the cleaned store and whether anything was removed. */
function prune(store: ApexPriceOverrideStore, now: number): { store: ApexPriceOverrideStore; changed: boolean } {
  const pruned: ApexPriceOverrideStore = {};
  let changed = false;

  Object.entries(store).forEach(([scopeKey, byDate]) => {
    const validEntries = Object.entries(byDate).filter(([, override]) => {
      const isValid = override.expiresAt > now;
      if (!isValid) changed = true;
      return isValid;
    });
    if (validEntries.length) {
      pruned[scopeKey] = Object.fromEntries(validEntries);
    }
  });

  return { store: pruned, changed };
}

/** Returns the non-expired overrides for a given search scope, keyed by dateKey (yyyy-MM-dd). */
export function getApexPriceOverrides(scopeKey: string): Map<string, ApexPriceOverride> {
  if (!isBrowser()) return new Map();
  const { store, changed } = prune(readStore(), Date.now());
  if (changed) writeStore(store);
  return new Map(Object.entries(store[scopeKey] ?? {}));
}

/**
 * Builds the override entry for a single day within a search scope and, unless persistence is turned
 * off, stores it in localStorage (refreshing the TTL). Always returns the entry so callers can keep an
 * in-memory copy in sync — this is what lets the calendar update live even when storage is disabled.
 */
export function setApexPriceOverride(
  scopeKey: string,
  dateKey: string,
  value: ApexPriceOverrideValue,
  { persist = true }: { persist?: boolean } = {}
): ApexPriceOverride {
  const expiresAt = Date.now() + TTL_MS;
  const entry: ApexPriceOverride =
    'unavailable' in value
      ? { unavailable: true, expiresAt }
      : { pricePerPerson: value.pricePerPerson, price: value.price, expiresAt };
  if (!persist || !isBrowser()) return entry;
  const { store } = prune(readStore(), Date.now());
  store[scopeKey] = { ...store[scopeKey], [dateKey]: entry };
  writeStore(store);
  return entry;
}

/** Removes the override for a single day within a search scope (e.g. when it matches the apex price again). */
export function removeApexPriceOverride(scopeKey: string, dateKey: string): void {
  if (!isBrowser()) return;
  const { store } = prune(readStore(), Date.now());
  if (!store[scopeKey]?.[dateKey]) return;
  delete store[scopeKey][dateKey];
  if (!Object.keys(store[scopeKey]).length) delete store[scopeKey];
  writeStore(store);
}
